import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '../utils/api'

// Omnichannel campaigns store (capability growth integration, Phase 1).
//
// Drives the create -> generate wave -> approve+schedule flow against
// /api/bwai/(sites/:id/campaigns | campaigns/*). generateWave is a slow call
// (Codex drafts one post per channel), so `generating` gates the UI; approve
// schedules real posts into Postiz, so the view confirms before calling it.
export const useCampaignsStore = defineStore('campaigns', () => {
  const list = ref([])
  const current = ref(null) // { ...campaign, waves: [{ ...wave, posts: [] }] }
  const calendar = ref([]) // all scheduled posts across the site's campaigns
  const loadingCalendar = ref(false)
  const loadingList = ref(false)
  const loadingCurrent = ref(false)
  const creating = ref(false)
  const generating = ref(false)
  const scheduling = ref(false)
  const observing = ref(false)
  const reports = ref({}) // waveId -> report
  const error = ref(null)

  const errMsg = (e) =>
    e?.response?.data?.error || e?.response?.data?.code || e?.message || 'Something went wrong'

  async function loadList(siteId) {
    loadingList.value = true
    error.value = null
    try {
      const { data } = await api.get(`/sites/${siteId}/campaigns`)
      list.value = data.campaigns || []
    } catch (e) {
      error.value = errMsg(e)
    } finally {
      loadingList.value = false
    }
  }

  async function create(siteId, payload) {
    creating.value = true
    error.value = null
    try {
      const { data } = await api.post(`/sites/${siteId}/campaigns`, payload)
      await loadList(siteId)
      return data.campaign
    } catch (e) {
      error.value = errMsg(e)
      throw e
    } finally {
      creating.value = false
    }
  }

  async function loadCampaign(id) {
    loadingCurrent.value = true
    error.value = null
    try {
      const { data } = await api.get(`/campaigns/${id}`)
      current.value = data.campaign
      // Auto-load results for any wave whose window is live/closed, so the
      // loop's metrics + recalibration surface without a manual collect.
      const waves = (data.campaign.waves || []).filter((w) =>
        ['scheduled', 'observing', 'evaluated'].includes(w.status))
      await Promise.all(waves.map((w) => loadReport(id, w.id)))
    } catch (e) {
      error.value = errMsg(e)
    } finally {
      loadingCurrent.value = false
    }
  }

  async function generateWave(id, guidance = '') {
    generating.value = true
    error.value = null
    try {
      await api.post(`/campaigns/${id}/waves/generate`, guidance ? { guidance } : {})
      await loadCampaign(id)
    } catch (e) {
      error.value = errMsg(e)
      throw e
    } finally {
      generating.value = false
    }
  }

  async function approveWave(id, waveId, opts = {}) {
    scheduling.value = true
    error.value = null
    try {
      await api.post(`/campaigns/${id}/waves/${waveId}/approve`, opts)
      await loadCampaign(id)
    } catch (e) {
      error.value = errMsg(e)
      throw e
    } finally {
      scheduling.value = false
    }
  }

  async function loadCalendar(siteId) {
    loadingCalendar.value = true
    error.value = null
    try {
      const { data } = await api.get(`/sites/${siteId}/campaigns/calendar`)
      calendar.value = data.posts || []
    } catch (e) {
      error.value = errMsg(e)
    } finally {
      loadingCalendar.value = false
    }
  }

  async function loadReport(id, waveId) {
    error.value = null
    try {
      const { data } = await api.get(`/campaigns/${id}/waves/${waveId}/report`)
      reports.value = { ...reports.value, [waveId]: data.report }
      return data.report
    } catch (e) {
      error.value = errMsg(e)
    }
  }

  async function observe(id, waveId) {
    observing.value = true
    error.value = null
    try {
      await api.post(`/campaigns/${id}/waves/${waveId}/observe`)
      await loadReport(id, waveId)
      await loadCampaign(id)
    } catch (e) {
      error.value = errMsg(e)
      throw e
    } finally {
      observing.value = false
    }
  }

  function clearCurrent() {
    current.value = null
    reports.value = {}
  }

  return {
    list, current, calendar, reports, loadingList, loadingCurrent, loadingCalendar,
    creating, generating, scheduling, observing, error,
    loadList, create, loadCampaign, generateWave, approveWave, loadCalendar,
    observe, loadReport, clearCurrent,
  }
})
